import type { RadioStation } from '../types/radio';

/**
 * Why this module exists
 * ----------------------
 * The app is served over https. A stream URL that starts with `http://` is a
 * *subresource* of an https page, so the browser blocks it as mixed content
 * before any request leaves the machine. Pasting the same URL into the address
 * bar is a top-level navigation, which browsers allow — that's why a station can
 * play in a tab and still fail in the app.
 *
 * So we try every https form of a station we can construct first. Many
 * Icecast/Shoutcast hosts serve the same mount over TLS, and Radio Browser
 * frequently has https in one of `url` / `url_resolved` and http in the other —
 * a build-time probe put that at roughly half of all http-only stations.
 *
 * For the rest, VITE_STREAM_PROXY can point at workers/stream-proxy, which
 * fetches http and serves https. It is optional: unset, http-only stations fail
 * as they always did, and the player offers an "open in new tab" link instead.
 */

export type UnplayableReason = 'mixed-content' | 'unreachable';

const isSecurePage = (): boolean =>
  typeof window !== 'undefined' && window.location.protocol === 'https:';

/** True when the browser will refuse to load this URL from the current page. */
export const isBlockedMixedContent = (url: string): boolean =>
  url.startsWith('http://') && isSecurePage();

const normalise = (raw: string | undefined | null): string | null => {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  // A bare host (no scheme) is far more likely to work over https than http.
  if (!/^https?:\/\//i.test(trimmed)) return `https://${trimmed}`;
  return trimmed;
};

/**
 * Every https form worth trying for an http URL, best first.
 *
 * A build-time probe of 250 random http-only stations found 49.6% are reachable
 * over https — but of those, only 23 answered on the original port while 99
 * answered on 443. Swapping the scheme while keeping the port, which is all this
 * used to do, therefore missed most of what is recoverable: a stream on
 * http://host:8000/live very often has TLS on https://host/live, not on 8000.
 *
 * Order matches the probe that produced those numbers, so the measured hit rate
 * carries over directly.
 */
const httpsVariants = (url: string): string[] => {
  if (!url.startsWith('http://')) return [];
  try {
    const parsed = new URL(url);
    const out: string[] = [];

    const withPort = (port: string) => {
      const candidate = new URL(url);
      candidate.protocol = 'https:';
      candidate.port = port;
      out.push(candidate.toString());
    };

    // TLS in place on the stream's own port.
    if (parsed.port && parsed.port !== '80') withPort(parsed.port);
    // The standard port — by far the most productive, and what a plain scheme
    // swap gives for a URL that carries no explicit port.
    withPort('');
    // A common alternative TLS port for stream servers.
    if (parsed.port !== '8443') withPort('8443');

    return [...new Set(out)];
  } catch {
    return [];
  }
};

/** The plain scheme swap: https on the standard port, no port guessing. */
const plainHttps = (url: string): string | null => {
  if (!url.startsWith('http://')) return null;
  try {
    const candidate = new URL(url);
    candidate.protocol = 'https:';
    candidate.port = '';
    return candidate.toString();
  } catch {
    return null;
  }
};

/**
 * The deployed stream proxy — see workers/stream-proxy.
 *
 * Baked in rather than left to configuration. VITE_STREAM_PROXY still overrides
 * it, but a build with no environment variable set now gets a working proxy
 * instead of silently falling back to "http-only stations do not play". Vite
 * inlines VITE_* at compile time, so an unset variable is indistinguishable from
 * a forgotten one, and the failure is invisible: stations simply keep not
 * playing, with nothing to say why.
 *
 * There is no secret here. It is a public URL that only fetches http streams,
 * refuses private and link-local addresses, and checks the calling origin.
 */
const DEFAULT_STREAM_PROXY = 'https://aurafy-stream-proxy.aurafy-stream-proxy.workers.dev';

/** Set VITE_STREAM_PROXY to `off` to disable proxying entirely. */
const configuredProxy = (import.meta.env?.VITE_STREAM_PROXY as string | undefined)?.trim();

const STREAM_PROXY: string | null =
  configuredProxy === 'off'
    ? null
    : (configuredProxy || DEFAULT_STREAM_PROXY).replace(/\/$/, '');

export const hasStreamProxy = (): boolean => STREAM_PROXY !== null;

/**
 * Route an http URL through the proxy.
 *
 * Only worth doing for http on a secure page: on http://localhost the direct URL
 * already works, and an https URL never needs it.
 */
const viaProxy = (url: string): string | null => {
  if (!STREAM_PROXY) return null;
  if (!url.startsWith('http://')) return null;
  if (!isSecurePage()) return null;
  return `${STREAM_PROXY}/?url=${encodeURIComponent(url)}`;
};

/**
 * Ordered list of URLs to try for a station, best first. Deduped, never empty
 * unless the station has no usable URL at all.
 *
 * Order matters: anything already https goes first (no upgrade guesswork), then
 * upgraded forms, then the raw http originals last. The http entries are kept
 * deliberately — they are harmless on an https page (blocked, we move on) and
 * they are the ones that actually work when running on http://localhost in dev.
 */
export const buildStreamCandidates = (station: RadioStation): string[] => {
  const primary = normalise(station.url_resolved);
  const secondary = normalise(station.url);

  // URLs the station itself already gives us over https. Anything here is
  // evidence, not a guess.
  const knownHttps = new Set(
    [primary, secondary].filter((u): u is string => !!u && !u.startsWith('http://'))
  );

  /**
   * Alternate TLS ports are a guess, and only worth making when there is no
   * evidence the host serves https on the standard port.
   *
   * Radio Browser routinely holds http in one URL field and https in the other
   * for the same address. Upgrading the http one then produced a :8443 guess at
   * a host already known to answer on 443 — so a BBC station listed four
   * candidates when it had two real endpoints, and every failure spent an extra
   * timeout on a port that was never going to be open. If 443 serves the path
   * and fails, 8443 on the same host will fail the same way.
   */
  const variantsFor = (url: string | null): string[] => {
    if (!url) return [];
    const plain = plainHttps(url);
    if (plain && knownHttps.has(plain)) return [];
    return httpsVariants(url);
  };

  const candidates: (string | null)[] = [
    primary && !primary.startsWith('http://') ? primary : null,
    secondary && !secondary.startsWith('http://') ? secondary : null,
    ...variantsFor(primary),
    ...variantsFor(secondary),
    // Last resort before giving up. Every direct https form has been tried by
    // this point, so the proxy only carries stations that genuinely have no
    // https endpoint — it is never in the path of a station that can reach the
    // listener directly.
    primary ? viaProxy(primary) : null,
    secondary ? viaProxy(secondary) : null,
    primary,
    secondary,
  ];

  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const candidate of candidates) {
    if (!candidate || seen.has(candidate)) continue;
    seen.add(candidate);
    ordered.push(candidate);
  }
  return ordered;
};

/**
 * HLS detection. Radio Browser's `hls` flag is authoritative when set, but it is
 * often 0 for stations that really are HLS, so the extension check stays too.
 */
export const isHlsUrl = (url: string, station?: RadioStation | null): boolean => {
  if (station?.hls === 1) return true;
  return /\.m3u8(\?|#|$)/i.test(url);
};

/**
 * `.pls` / `.m3u` are playlist files, not streams — handing one to <audio>
 * produces "no supported source". They point at the real stream, so we fetch and
 * dereference them (see parsePlaylist). Note `.m3u8` is deliberately excluded:
 * that's HLS and hls.js handles it.
 */
export const isPlaylistUrl = (url: string): boolean =>
  /\.(pls|m3u)(\?|#|$)/i.test(url);

/**
 * Pull stream URLs out of a `.pls` or `.m3u` body.
 *
 * PLS is INI-like with `File1=`, `File2=` … entries. M3U is line-based, where
 * anything that isn't a `#` directive is a URL. Both may carry relative paths,
 * which are resolved against the playlist's own address.
 *
 * Entries are returned in file order, capped, and https-preferred like any other
 * candidate.
 */
export const parsePlaylist = (body: string, playlistUrl: string, max = 3): string[] => {
  const found: string[] = [];

  for (const rawLine of body.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;

    let value: string | null = null;
    const plsMatch = line.match(/^File\d*\s*=\s*(.+)$/i);
    if (plsMatch) {
      value = plsMatch[1].trim();
    } else if (!line.startsWith('#') && !line.includes('=') && !line.startsWith('[')) {
      value = line; // plain m3u entry
    }
    if (!value) continue;
    // Prose can't be a URL, and `new URL()` would happily percent-encode a
    // sentence into a valid-looking address. Stream URLs never contain spaces.
    if (/\s/.test(value)) continue;

    try {
      // Absolute URLs pass through; relative ones resolve against the playlist.
      const resolved = new URL(value, playlistUrl).toString();
      if (/^https?:/i.test(resolved) && !found.includes(resolved)) found.push(resolved);
    } catch {
      // Not a URL — skip it.
    }
  }

  return found.slice(0, max);
};

/** https forms first, then the original, for a single URL. Deduped. */
export const httpsFirst = (url: string): string[] => {
  const normalised = normalise(url);
  if (!normalised) return [];
  return [...new Set([...httpsVariants(normalised), normalised])];
};

/**
 * Why a station couldn't be played, once every candidate has been exhausted.
 *
 * Judged on the station's *own* URLs, not the candidate ladder. The ladder
 * contains https forms we synthesised ourselves, and a station that only ever
 * offered http would otherwise be classified 'unreachable' purely because our
 * own guess was https — telling the user the stream is down when the truth is
 * that their browser refused to load it. That is the difference between "this
 * station is broken" and "this station works, open it in a tab".
 */
export const classifyFailure = (station: RadioStation | null): UnplayableReason => {
  if (!station) return 'unreachable';

  const originals = [station.url_resolved, station.url]
    .map((value) => normalise(value))
    .filter((value): value is string => value !== null);

  if (originals.length === 0) return 'unreachable';
  // With a proxy configured, http was reachable and still failed, so the mixed
  // content rule is no longer the explanation — telling the user to open it in a
  // tab would be wrong, because that will not work either.
  if (hasStreamProxy()) return 'unreachable';
  return originals.every(isBlockedMixedContent) ? 'mixed-content' : 'unreachable';
};

/** The URL to offer as "open in a new tab", which bypasses the mixed-content rule. */
export const externalListenUrl = (station: RadioStation): string | null =>
  normalise(station.url_resolved) ?? normalise(station.url);
